/**
 * @theme/main-search — extends SearchForm; keeps multiple search inputs in sync (header search vs.
 * full search-page input). Mobile: scrolls input into view on focus.
 */

import { defineComponent } from '@theme/component';
import { SearchForm } from '@theme/search-form';

/**
 * @extends {SearchForm}
 */
export class MainSearch extends SearchForm {
  /** @type {NodeListOf<HTMLInputElement> | null} */ allSearchInputs = null;

  connectedCallback() {
    super.connectedCallback();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.input?.addEventListener('focus', this.#onFocus);
    if (this.allSearchInputs.length < 2) return;
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.#onAnyInput));
    this.allSearchInputs.forEach((input) => input.form?.addEventListener('reset', this.#onAnyReset));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.input?.removeEventListener('focus', this.#onFocus);
    this.allSearchInputs?.forEach((input) => input.removeEventListener('input', this.#onAnyInput));
  }

  /** @param {Event} event */
  #onAnyInput = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    this.#keepInSync(target.value, target);
  };

  #onAnyReset = () => this.#keepInSync('', this.input);

  #onFocus = () => {
    if (window.innerWidth < 750) this.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * @param {string} value
   * @param {HTMLInputElement | null} source
   */
  #keepInSync(value, source) {
    this.allSearchInputs?.forEach((input) => {
      if (input !== source) input.value = value;
    });
  }
}

defineComponent('main-search', MainSearch);
